import React from 'react';
import { useQuery } from 'react-query';
import { listRentRollImports } from '~/api/resident';
import { PreviewSummary } from './RentRollConfirmModal';
import classes from './UploadRentRoll.module.css';

export type RentRollImportStatus =
  | 'previewed'
  | 'processing'
  | 'done'
  | 'failed'
  | 'expired';

export type RentRollImportRow = {
  uuid: string;
  building: string;
  buildingName: string | null;
  status: RentRollImportStatus;
  uploadedBy: string;
  uploadedByName: string | null;
  uploadedByEmail: string | null;
  confirmedBy: string | null;
  confirmedByName: string | null;
  summary: PreviewSummary;
  expiresAt: string | null;
  createdAt: string;
  isExpired: boolean;
  fileUrl: string | null;
};

const STATUS_LABELS: Record<RentRollImportStatus, string> = {
  previewed: 'Pending',
  processing: 'Processing',
  done: 'Done',
  failed: 'Failed',
  expired: 'Expired'
};

const STATUS_BADGE_SUFFIX: Record<RentRollImportStatus, string> = {
  previewed: 'Pending',
  processing: 'Processing',
  done: 'Done',
  failed: 'Failed',
  expired: 'Expired'
};

type RentRollImportsListProps = {
  buildingId: string;
  onReview: (preview: RentRollImportRow) => void;
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const RentRollImportsList = ({
  buildingId,
  onReview
}: RentRollImportsListProps) => {
  const { data, isLoading } = useQuery<RentRollImportRow[]>(
    ['rentRollImports', buildingId],
    () => listRentRollImports(buildingId).then((res) => res.data.previews),
    { refetchOnWindowFocus: false }
  );

  const importsForBuilding = data ?? [];

  if (isLoading || importsForBuilding.length === 0) return null;

  return (
    <div className={classes.pendingImportsWrapper}>
      <div className={classes.pendingImportsTitle}>
        Rent roll imports ({importsForBuilding.length})
      </div>
      <table className={classes.pendingImportsTable}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Uploaded by</th>
            <th>Confirmed by</th>
            <th>Incoming rows</th>
            <th>Created</th>
            <th>Expires</th>
            <th>File</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {importsForBuilding.map((preview) => {
            const isActionable =
              preview.status === 'previewed' && !preview.isExpired;
            const effectiveStatus = preview.isExpired
              ? 'expired'
              : preview.status;
            const statusLabel = STATUS_LABELS[effectiveStatus];
            return (
              <tr key={preview.uuid}>
                <td>
                  <span
                    className={`${classes.statusBadge} ${
                      classes[
                        `statusBadge${STATUS_BADGE_SUFFIX[effectiveStatus]}`
                      ]
                    }`}
                  >
                    {statusLabel}
                  </span>
                </td>
                <td>
                  {preview.uploadedByName ||
                    preview.uploadedByEmail ||
                    'Unknown'}
                </td>
                <td>{preview.confirmedByName || '—'}</td>
                <td>{preview.summary.totalIncomingRows}</td>
                <td>{formatDate(preview.createdAt)}</td>
                <td>{formatDate(preview.expiresAt)}</td>
                <td>
                  {preview.fileUrl && (
                    <a
                      href={preview.fileUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      View file
                    </a>
                  )}
                </td>
                <td>
                  {isActionable && (
                    <button
                      className='btn-standard btn-primary btn-small'
                      onClick={() => onReview(preview)}
                    >
                      Review
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RentRollImportsList;
